import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandshake, faEnvelope, faArrowRight, faSpinner, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { Button } from '../components/common/Button';
import { GlassCard } from '../components/common/GlassCard';
import { FadeUp } from '../components/common/FadeUp';

export function Login() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, verify } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const tokenParam = searchParams.get('token');
  if (tokenParam && !verifying) {
    setVerifying(true);
    verify(tokenParam).then(() => {
      addToast('success', '다시 오신 것을 환영합니다!');
      navigate('/dashboard');
    }).catch(() => addToast('error', '유효하지 않거나 만료된 링크입니다'));
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const result = await login(email.trim());
      setSent(true);
      if (result.token) {
        await verify(result.token);
        addToast('success', '로그인되었습니다!');
        navigate('/dashboard');
      }
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : '링크 전송에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main id="main-content" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-4)' }}>
      <FadeUp>
        <GlassCard style={{ width: '100%', maxWidth: '420px', padding: 'var(--space-10)' }}>
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-8)' }}>
            <FontAwesomeIcon icon={faHandshake} style={{ fontSize: 'var(--text-4xl)', color: 'var(--color-accent)', marginBottom: 'var(--space-4)' }} />
            <h1 style={{ fontSize: 'var(--text-2xl)', marginBottom: 'var(--space-2)' }}>딜룸에 오신 것을 환영합니다</h1>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
              {sent ? '이메일에서 매직 링크를 확인하세요' : '이메일로 로그인하세요'}
            </p>
          </div>

          {sent ? (
            <div style={{ textAlign: 'center' }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 'var(--text-4xl)', color: 'var(--color-success)', marginBottom: 'var(--space-4)' }} />
              <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
                <strong>{email}</strong>으로 매직 링크를 보냈습니다
              </p>
              <button onClick={() => setSent(false)} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer', fontSize: 'var(--text-sm)', marginTop: 'var(--space-4)' }}>
                다른 이메일로 시도
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ position: 'relative', marginBottom: 'var(--space-6)' }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ position: 'absolute', left: 'var(--space-4)', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com" required autoFocus aria-label="이메일 주소"
                  style={{ width: '100%', padding: 'var(--space-3) var(--space-4) var(--space-3) var(--space-10)', fontSize: 'var(--text-base)', fontFamily: 'var(--font-sans)', color: 'var(--color-text-primary)', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <Button type="submit" variant="primary" size="lg" disabled={loading} icon={<FontAwesomeIcon icon={loading ? faSpinner : faArrowRight} spin={loading} />} style={{ width: '100%', justifyContent: 'center' }}>
                {loading ? '전송 중...' : '이메일로 계속하기'}
              </Button>
            </form>
          )}

          <p style={{ textAlign: 'center', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-6)' }}>
            로그인하면 이용약관 및 개인정보처리방침에 동의하는 것으로 간주됩니다
          </p>
        </GlassCard>
      </FadeUp>
    </main>
  );
}
